import translations from '../../../translations';
import { isNullOrUndefined } from '../../../utils/guard';
import { importThemeConfig } from '../../../sources/theme';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import { useState } from 'react';
import type { UserTenantResponseAPI } from '../../../types/user';

type Props = {
    tenant: UserTenantResponseAPI;
    onImportSuccess?: () => void;
};

const useThemeImport = ({ tenant, onImportSuccess }: Props) => {
    const [importTheme, setImportTheme] = useState<string | null>();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const dispatchNotification = useNotificationDispatch();

    const onDrop = (files: File[]) => {
        // Only accept one file at a time for now.
        if (files.length === 1) {
            const reader = new FileReader();
            reader.onloadend = (response) => {
                if (response?.target?.result) {
                    setImportTheme(response.target.result.toString());
                    setShowConfirmation(true);
                }
            };

            if (files[0]) {
                reader.readAsText(files[0]);
            }
        }
    };

    const onConfirmImport = async () => {
        try {
            if (importTheme) {
                await importThemeConfig(importTheme);
                dispatchNotification({
                    type: 'notifySuccess',
                    payload: {
                        message: translations.THEMES_import_success,
                    },
                });
                onImportSuccess?.();
            }
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setImportTheme(null);
            setShowConfirmation(false);
        }
    };

    const onCancelImport = () => {
        setShowConfirmation(false);
        setImportTheme(null);
    };

    const getNameFromThemeConfig = () => {
        if (isNullOrUndefined(importTheme)) {
            return null;
        }

        try {
            return JSON.parse(JSON.parse(importTheme) as string)?.developerName ?? null;
        } catch (_error) {
            return null;
        }
    };

    const importedThemeName = getNameFromThemeConfig();

    const importMessage: string =
        isNullOrUndefined(importedThemeName) === false
            ? translations.THEMES_confirm_import_message.replace(
                  '${themeName}',
                  importedThemeName ?? '',
              ).replace('${tenant}', tenant.developerName)
            : translations.THEMES_confirm_import_message_without_name.replace(
                  '${tenant}',
                  tenant.developerName,
              );

    return {
        importTheme,
        showConfirmation,
        importedThemeName,
        importMessage,
        onConfirmImport,
        onCancelImport,
        onDrop,
    };
};
export default useThemeImport;
